import { useEffect, useRef } from 'react';
import { Point } from '../../../common/maths/geometry/pointTypes';

type TouchPoint = {
    identifier: number;
    start: {
        x: number;
        y: number;
        time: number;
    };
    end?: {
        x: number;
        y: number;
        time: number;
    };
};

const averagePoint = (points: Point[]): Point => {
    const averagePoint = points.reduce(
        (acc, point) => {
            acc.x += point.x;
            acc.y += point.y;
            return acc;
        },
        { x: 0, y: 0 },
    );

    averagePoint.x = averagePoint.x / points.length;
    averagePoint.y = averagePoint.y / points.length;

    return averagePoint;
};

const useTapGestureRecognizer = (
    targetRef: React.RefObject<HTMLElement>,
    onGesture: ({
        touchPoints,
        location,
        target,
    }: {
        touchPoints: TouchPoint[];
        location: Point;
        target: EventTarget;
    }) => void,
    options = {
        requiredTouches: 2, // Number of required touches for the gesture
        maxTouchDuration: 500, // Maximum duration for the gesture in ms
        maxMovementThreshold: 20, // Maximum allowed finger movement in pixels
    },
) => {
    const { requiredTouches, maxTouchDuration, maxMovementThreshold } = options;

    const eventTargetRef = useRef<EventTarget | undefined | null>(null);
    const touchPointsRef = useRef<TouchPoint[]>([]);

    const handleTouchStart = (event: TouchEvent) => {
        if (!event.touches || event.touches.length !== requiredTouches) {
            touchPointsRef.current = [];
            return;
        }

        const currentTime = performance.now();

        // Record initial touch positions and time
        touchPointsRef.current = Array.from(event.touches).map((touch) => ({
            identifier: touch.identifier,
            start: {
                x: touch.clientX,
                y: touch.clientY,
                time: currentTime,
            },
        }));

        eventTargetRef.current = event.touches.item(0)?.target;
    };

    const handleTouchEnd = (event: TouchEvent) => {
        if (touchPointsRef.current.length !== requiredTouches) return;

        const eventTarget = eventTargetRef.current;
        if (!eventTarget) return;

        const currentTime = performance.now();

        const changedTouches = Array.from(event.changedTouches);

        touchPointsRef.current = touchPointsRef.current.map((point) => {
            const updatedTouch = changedTouches.find((touch) => touch.identifier === point.identifier);
            if (!updatedTouch) return point;

            return {
                ...point,
                end: {
                    x: updatedTouch.clientX,
                    y: updatedTouch.clientY,
                    time: currentTime,
                },
            };
        });

        // if all touches have ended, check if the gesture is valid
        const allTouchesEnded = touchPointsRef.current.every((point) => !!point.end);
        if (!allTouchesEnded) return;

        const touchDuration = touchPointsRef.current[0].end!.time - touchPointsRef.current[0].start.time;

        // Check if touch duration is within limit
        if (touchDuration > maxTouchDuration) {
            touchPointsRef.current = [];
            return;
        }

        // Check movement for each touch point
        const withinMovementThreshold = touchPointsRef.current.every((touch) => {
            const movement = Math.sqrt(
                Math.pow(touch.start.x - touch.end!.x, 2) + Math.pow(touch.start.y - touch.end!.y, 2),
            );

            return movement <= maxMovementThreshold;
        });

        if (!withinMovementThreshold) {
            touchPointsRef.current = [];
            return;
        }

        // Calculate average point of the two touches
        const endPoints = touchPointsRef.current.map((point) => point.end!);
        const averageLocation = averagePoint(endPoints);

        // Fire the gesture callback
        onGesture({ touchPoints: touchPointsRef.current, location: averageLocation, target: eventTarget });

        touchPointsRef.current = [];
    };

    const handleTouchCancel = () => {
        touchPointsRef.current = [];
    };

    useEffect(() => {
        const target = targetRef.current;
        if (!target) return;

        target.addEventListener('touchstart', handleTouchStart, { passive: true });
        target.addEventListener('touchend', handleTouchEnd, { passive: true });
        target.addEventListener('touchcancel', handleTouchCancel, { passive: true });

        return () => {
            target.removeEventListener('touchstart', handleTouchStart);
            target.removeEventListener('touchend', handleTouchEnd);
            target.removeEventListener('touchcancel', handleTouchCancel);
        };
    }, [options.maxTouchDuration, options.maxMovementThreshold]);
};

export default useTapGestureRecognizer;
